import { supabase } from "../lib/supabase";

const FALLBACK_ADMIN_EMAILS = ["infocolobongo@gmail.com", "infocolobongo@gmail.ca"];
const ADMIN_ROLE_VALUES = new Set(["admin", "super_admin", "owner"]);

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRole(value) {
  return String(value || "").trim().toLowerCase();
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["1", "true", "yes", "y"].includes(normalized);
  }
  return false;
}

function parseEnvAdminEmails() {
  const raw = String(process.env.REACT_APP_ADMIN_EMAILS || "");
  if (!raw.trim()) return [];
  return raw
    .split(",")
    .map((email) => normalizeEmail(email))
    .filter(Boolean);
}

export function getAdminEmailAllowlist() {
  return Array.from(new Set([...parseEnvAdminEmails(), ...FALLBACK_ADMIN_EMAILS.map(normalizeEmail)]));
}

function extractRoleFromObject(source) {
  if (!source || typeof source !== "object") return "";

  const directRole = normalizeRole(source.role || source.app_role || source.user_role);
  if (directRole) return directRole;

  if (toBoolean(source.is_admin)) return "admin";
  if (toBoolean(source.admin)) return "admin";

  return "";
}

export async function fetchUserProfile(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role, is_admin, admin, artist_name, studio_portes_vues, contributor_form, contributor_form_prompt_seen")
      .eq("id", userId)
      .maybeSingle();

    if (error) return null;
    return data || null;
  } catch (_error) {
    return null;
  }
}

export function isAdminProfile(profile = null) {
  const resolvedRole = extractRoleFromObject(profile);
  return ADMIN_ROLE_VALUES.has(resolvedRole);
}

export function resolveAdminAccess({ user, profile = null }) {
  const email = normalizeEmail(user?.email);
  const allowlist = getAdminEmailAllowlist();
  const emailMatch = allowlist.includes(email);

  const roleFromProfile = extractRoleFromObject(profile);
  const roleFromMetadata = extractRoleFromObject(user?.user_metadata) || extractRoleFromObject(user?.app_metadata);
  const resolvedRole = roleFromProfile || roleFromMetadata || "user";

  const roleMatch = ADMIN_ROLE_VALUES.has(resolvedRole);
  const isAdmin = Boolean(roleMatch || emailMatch);
  const reason = roleMatch ? "role" : emailMatch ? "allowlist_email" : "none";

  return {
    isAdmin,
    role: resolvedRole,
    reason,
    email,
    allowlist
  };
}

export async function resolveCurrentSessionAdminState() {
  const {
    data: { user },
    error
  } = await supabase.auth.getUser();

  if (error || !user) {
    return {
      user: null,
      profile: null,
      isAdmin: false,
      role: "guest",
      reason: "no_session"
    };
  }

  const profile = await fetchUserProfile(user.id);
  const access = resolveAdminAccess({ user, profile });

  return {
    user,
    profile,
    ...access
  };
}
